import { DOCUMENT } from '@angular/common';
import { Injectable, Type, inject, signal } from '@angular/core';
import { SID } from '../interfaces';

/**
 * Resolves the active brand's whitelabel header via dynamic import so a single
 * tenant no longer ships all twelve brand headers inside the eager header
 * chunk. `init()` runs from an app initializer (see app.config.ts): bootstrap
 * waits for the matching brand chunk on both server and browser, so the SSR
 * output and the hydration render agree and no other brand's code is loaded.
 */
const registry: Array<{
  match: (sid: string, host: string) => boolean;
  load: () => Promise<Type<unknown>>;
}> = [
  {
    match: (sid) => sid === 'dubois',
    load: () => import('./white-label-header-dubois.component').then((m) => m.WhiteLabelHeaderDuboisComponent),
  },
  {
    match: (sid) => sid === 'peoples_onlineauctions_com',
    load: () =>
      import('./white-label-header-peoples-online-auctions.component').then(
        (m) => m.WhiteLabelHeaderPeoplesOnlineAuctionsComponent,
      ),
  },
  {
    match: (sid) => sid === 'jennings_auctions_online_appspot_com',
    load: () =>
      import('./white-label-header-jennings-auctions.component').then(
        (m) => m.WhiteLabelHeaderJenningsOnlineAuctionsComponent,
      ),
  },
  {
    match: (sid) => sid === 'bidnowllc_appspot_com',
    load: () => import('./white-label-header-bidnowllc.component').then((m) => m.WhiteLabelHeaderBidnowllcComponent),
  },
  {
    match: (sid) => sid === 'bids_entrustedauctions_com',
    load: () =>
      import('./white-label-header-entrusted-auctions.component').then(
        (m) => m.WhiteLabelHeaderEntrustedAuctionsComponent,
      ),
  },
  {
    match: (sid) => sid === 'bids_farmerauctionsonline_com',
    load: () =>
      import('./white-label-header-farmers-auctions.component').then(
        (m) => m.WhiteLabelHeaderFarmersAuctionsComponent,
      ),
  },
  {
    match: (sid) => sid === 'quinnsauction_appspot_com',
    load: () =>
      import('./white-label-header-quinns-auctions.component').then((m) => m.WhiteLabelHeaderQuinnsAuctionsComponent),
  },
  {
    match: (sid, host) => sid === 'selldudleyresources' && host === 'selldudleyresources.com',
    load: () =>
      import('./white-label-header-selldudleyresources.component').then(
        (m) => m.WhiteLabelHeaderSellDudleyResourcesComponent,
      ),
  },
  {
    match: (sid, host) => sid === 'selldudleyresources' && host !== 'selldudleyresources.com',
    load: () =>
      import('./white-label-header-auctionprosrva.component').then((m) => m.WhiteLabelHeaderAuctionProsRVAComponent),
  },
  {
    match: (sid) => sid === 'cal_auctions_appspot_com',
    load: () =>
      import('./white-label-header-calauctions.component').then((m) => m.WhiteLabelHeaderCalAuctionsComponent),
  },
  {
    match: (sid) => sid === 'bidon_hurleyauctions_com',
    load: () =>
      import('./white-label-header-hurleyauctions.component').then((m) => m.WhiteLabelHeaderHurleyAuctionsComponent),
  },
  {
    match: (sid, host) => sid === 'rasmus_auctions_appspot_com' && host === 'ararity.auction-engine.com',
    load: () =>
      import('./white-label-header-ararity-auctions.component').then(
        (m) => m.WhiteLabelHeaderArarityAuctionsComponent,
      ),
  },
];

@Injectable({ providedIn: 'root' })
export class WhiteLabelHeaderLoader {
  /** Optional so component test beds without a SID provider can construct the header. */
  private readonly sid = inject(SID, { optional: true }) ?? '';
  private readonly document = inject(DOCUMENT);

  /** The active brand's header component, or null for the default (Rasmus) shell. */
  readonly component = signal<Type<unknown> | null>(null);

  async init(): Promise<void> {
    const host = this.document?.location?.hostname ?? '';
    const entry = registry.find((e) => e.match(this.sid, host));
    if (!entry) {
      return;
    }
    try {
      this.component.set(await entry.load());
    } catch {
      // Chunk failed to load — the header falls back to the default shell.
    }
  }
}
